import React, { useState, useEffect, useMemo, useRef } from "react";
import { KeenIcon } from "@/components";
import { FormattedMessage, useIntl } from "react-intl";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "datatables.net-colreorder-dt";

const ColumnToggleDropdown = ({
  tableRef,
  columns = [],
  defaultVisibleColumns = [],
  storageKey = null,
  pinnedFirstColumns = [],
  pinnedLastColumns = [],
}) => {
  const [visibleCols, setVisibleCols] = useState([]);
  const [orderedCols, setOrderedCols] = useState([]);
  const [search, setSearch] = useState("");
  const { locale, formatMessage } = useIntl();
  const [resolvedStorageKey, setResolvedStorageKey] = useState(null);
  const visibilityAppliedRef = useRef(false);
  const hydratingVisibilityRef = useRef(false);
  const suppressPersistRef = useRef(false);

  const resolveStorageKey = () => resolvedStorageKey;

  const resolveVisibilityKey = () => {
    const key = resolveStorageKey();
    return key ? `${key}:visible` : null;
  };

  useEffect(() => {
    if (storageKey) {
      if (resolvedStorageKey !== storageKey) {
        setResolvedStorageKey(storageKey);
      }
      return;
    }
    let cancelled = false;
    const resolveWhenReady = () => {
      if (cancelled) return;
      const tableId = tableRef?.current?.dt?.()?.table?.()?.node?.()?.id;
      if (!tableId) {
        setTimeout(resolveWhenReady, 50);
        return;
      }
      const pathKey =
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.search || ""}`
          : "table";
      const nextKey = `column-toggle-order:${pathKey}:${tableId}`;
      if (resolvedStorageKey !== nextKey) {
        setResolvedStorageKey(nextKey);
      }
    };
    resolveWhenReady();
    return () => {
      cancelled = true;
    };
  }, [storageKey, tableRef, resolvedStorageKey]);

  const normalizePinned = (list) => {
    if (!Array.isArray(list) || list.length === 0) return list;
    const firstSet = new Set(Array.isArray(pinnedFirstColumns) ? pinnedFirstColumns : []);
    const lastSet = new Set(Array.isArray(pinnedLastColumns) ? pinnedLastColumns : []);
    const first = [];
    const normal = [];
    const last = [];

    list.forEach((c) => {
      if (!c?.name) return;
      if (firstSet.has(c.name)) {
        first.push(c);
        return;
      }
      if (lastSet.has(c.name)) {
        last.push(c);
        return;
      }
      normal.push(c);
    });

    return [...first, ...normal, ...last];
  };

  const mergeVisibleWithDefaults = (savedVisible, allNamedColumns) => {
    const allNames = new Set(allNamedColumns.map((c) => c.name));
    const filteredSaved = (Array.isArray(savedVisible) ? savedVisible : []).filter((name) => allNames.has(name));
    const defaults = allNamedColumns
      .filter((c) => defaultVisibleColumns.includes(c.name))
      .map((c) => c.name);
    const merged = [...filteredSaved];
    defaults.forEach((name) => {
      if (!merged.includes(name)) merged.push(name);
    });
    return merged;
  };

  const getOrderedColumns = () => {
    const named = columns.filter((c) => c.name);
    const key = resolveStorageKey();
    if (!key) return normalizePinned(named);
    const raw = typeof window !== "undefined" ? localStorage.getItem(key) : null;
    if (!raw) return normalizePinned(named);
    try {
      const saved = JSON.parse(raw);
      if (!Array.isArray(saved) || saved.length === 0) return normalizePinned(named);
      const map = new Map(named.map((c) => [c.name, c]));
      const ordered = [];
      saved.forEach((name) => {
        const col = map.get(name);
        if (col) ordered.push(col);
      });
      named.forEach((c) => {
        if (!saved.includes(c.name)) ordered.push(c);
      });
      return normalizePinned(ordered);
    } catch {
      return normalizePinned(named);
    }
  };

  const applyColumnOrder = (nextOrderedCols, shouldDraw = false) => {
    const table = tableRef?.current?.dt?.();
    if (!table || !table.colReorder || !Array.isArray(nextOrderedCols)) return;
    const currentIndexes = table.columns().indexes().toArray();
    const orderedIndexes = [];
    const seen = new Set();

    const pushIndex = (idx) => {
      if (idx === undefined || idx === null || seen.has(idx)) return;
      seen.add(idx);
      orderedIndexes.push(idx);
    };

    nextOrderedCols.forEach((c) => {
      if (!c?.name) return;
      pushIndex(table.column(`${c.name}:name`).index());
    });

    currentIndexes.forEach((idx) => pushIndex(idx));

    // ColReorder throws "column count mismatch" if the order array length differs
    // from the table's column count. Bail out rather than warn if it can't match
    // exactly (e.g. stale saved order after columns were added/removed).
    if (orderedIndexes.length !== currentIndexes.length) return;

    if (orderedIndexes.length) {
      table.colReorder.order(orderedIndexes);
      if (shouldDraw) {
        table.columns.adjust().draw(false);
      } else {
        table.columns.adjust();
      }
    }
  };



  const getVisibleFromTable = () => {
    const table = tableRef?.current?.dt?.();
    if (!table) return null;
    return columns
      .filter((c) => c.name)
      .filter((c) => {
        const colIndex = table.column(`${c.name}:name`).index();
        if (colIndex === undefined || colIndex === null) return false;
        return table.column(colIndex).visible();
      })
      .map((c) => c.name);
  };

  const getSavedVisibility = () => {
    const key = resolveVisibilityKey();
    if (!key) return null;
    const raw = typeof window !== "undefined" ? localStorage.getItem(key) : null;
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  };

  const applyVisibility = (names, shouldDraw = false) => {
    const table = tableRef?.current?.dt?.();
    if (!table || !Array.isArray(names)) return;
    const nameSet = new Set(names);
    columns.forEach((c) => {
      if (!c.name) return;
      const colIndex = table.column(`${c.name}:name`).index();
      if (colIndex === undefined || colIndex === null) return;
      table.column(colIndex).visible(nameSet.has(c.name), false);
    });
    if (shouldDraw) {
      table.columns.adjust().draw(false);
    } else {
      table.columns.adjust();
    }
  };

  // initialize visible columns
  useEffect(() => {
    visibilityAppliedRef.current = false;
    if (columns.length) {
      const ordered = getOrderedColumns();
      setOrderedCols(ordered);

      const savedVisible = getSavedVisibility();
      if (savedVisible) {
        const namedColumns = columns.filter((c) => c.name);
        const merged = mergeVisibleWithDefaults(savedVisible, namedColumns);
        if (merged.length) {
          hydratingVisibilityRef.current = true;
          setVisibleCols(merged);
          applyVisibility(merged, false);
          return;
        }
      }
      const initialVisible = columns
        .filter((c) => c.name && defaultVisibleColumns.includes(c.name))
        .map((c) => c.name);
      const tableVisible = getVisibleFromTable();
      const shouldUseDefaults =
        initialVisible.length > 0 &&
        Array.isArray(tableVisible) &&
        tableVisible.length === columns.filter((c) => c.name).length &&
        initialVisible.length < tableVisible.length;

      if (shouldUseDefaults) {
        hydratingVisibilityRef.current = true;
        setVisibleCols(initialVisible);
        applyVisibility(initialVisible, false);
        return;
      }

      if (tableVisible !== null) {
        hydratingVisibilityRef.current = true;
        setVisibleCols(tableVisible);
        return;
      }

      hydratingVisibilityRef.current = true;
      setVisibleCols(initialVisible);
      applyVisibility(initialVisible, false);
    }
  }, [columns, defaultVisibleColumns, tableRef, resolvedStorageKey]);

  // Ensure saved visibility wins after DataTable initComplete runs
  useEffect(() => {
    const table = tableRef?.current?.dt?.();
    if (!table) return;
    const savedVisible = getSavedVisibility();
    if (!savedVisible || savedVisible.length === 0) return;

    const namedColumns = columns.filter((c) => c.name);
    const merged = mergeVisibleWithDefaults(savedVisible, namedColumns);
    if (!merged.length) return;

    const apply = () => {
      hydratingVisibilityRef.current = true;
      setVisibleCols(merged);
      applyVisibility(merged, true);
    };

    const settings = table.settings?.()[0];
    if (settings && settings._bInitComplete) {
      apply();
      return;
    }

    const handler = () => apply();
    table.on("init.dt", handler);
    return () => {
      table.off("init.dt", handler);
    };
  }, [columns, tableRef]);

  // Persist order whenever it changes
  useEffect(() => {
    if (!orderedCols.length) return;
    const key = resolveStorageKey();
    if (!key) return;
    if (typeof window !== "undefined") {
      const nextNames = orderedCols.map((c) => c.name);
      let saved = null;
      try {
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) saved = parsed;
        }
      } catch {
        saved = null;
      }

      // Avoid overwriting a fuller saved order with a partial list
      if (saved && saved.some((name) => !nextNames.includes(name))) {
        return;
      }

      localStorage.setItem(key, JSON.stringify(nextNames));
    }
  }, [orderedCols]);

  useEffect(() => {
    if (!orderedCols.length) return;
    applyColumnOrder(orderedCols, false);
  }, [orderedCols]);


  useEffect(() => {
    if (!visibleCols.length || visibilityAppliedRef.current) return;
    const table = tableRef?.current?.dt?.();
    if (!table) return;
    applyVisibility(visibleCols, false);
    visibilityAppliedRef.current = true;
  }, [visibleCols, tableRef]);

  useEffect(() => {
    if (!orderedCols.length) return;
    const key = resolveVisibilityKey();
    if (!key) return;
    if (typeof window !== "undefined") {
      if (hydratingVisibilityRef.current) {
        hydratingVisibilityRef.current = false;
        return;
      }
      if (suppressPersistRef.current) {
        suppressPersistRef.current = false;
        return;
      }
      localStorage.setItem(key, JSON.stringify(visibleCols));
    }
  }, [visibleCols, orderedCols]);

  const handleMenuOpenChange = (open) => {
    if (!open) return;
    if (!resolvedStorageKey && !storageKey) {
      const tableId = tableRef?.current?.dt?.()?.table?.()?.node?.()?.id;
      if (tableId) {
        const pathKey =
          typeof window !== "undefined"
            ? `${window.location.pathname}${window.location.search || ""}`
            : "table";
        setResolvedStorageKey(`column-toggle-order:${pathKey}:${tableId}`);
      }
    }
    const tableVisible = getVisibleFromTable();
    if (tableVisible !== null) {
      suppressPersistRef.current = true;
      setVisibleCols(tableVisible);
    }
  };

  const handleToggle = (colName) => {
    const table = tableRef.current.dt();
    const colIndex = table.column(`${colName}:name`).index();
    const visible = table.column(colIndex).visible();
    table.column(colIndex).visible(!visible, false);
    table.columns.adjust().draw(false);

    setVisibleCols((prev) =>
      visible ? prev.filter((c) => c !== colName) : [...prev, colName]
    );
  };

  const handleSelectAll = () => {
    const table = tableRef.current.dt();
    orderedCols.forEach((c) => {
      if (c.name) {
        const colIndex = table.column(`${c.name}:name`).index();
        table.column(colIndex).visible(true, false);
      }
    });
    table.columns.adjust().draw(false);
    setVisibleCols(orderedCols.filter((c) => c.name).map((c) => c.name));
  };

  const handleResetDefault = () => {
    const table = tableRef.current.dt();
    orderedCols.forEach((c) => {
      if (c.name) {
        const colIndex = table.column(`${c.name}:name`).index();
        table.column(colIndex).visible(defaultVisibleColumns.includes(c.name), false);
      }
    });
    table.columns.adjust().draw(false);
    setVisibleCols(defaultVisibleColumns);
  };

  const handleResetOrdering = () => {
    const named = columns.filter((c) => c.name);
    const normalized = normalizePinned(named);
    setOrderedCols(normalized);
    const key = resolveStorageKey();
    if (key && typeof window !== "undefined") {
      localStorage.removeItem(key);
    }
    applyColumnOrder(normalized, true);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const filteredColumns = useMemo(
    () =>
      orderedCols.filter(
        (c) =>
          c.name &&
          c.title?.toLowerCase().includes(search.toLowerCase().trim())
      ),
    [orderedCols, search]
  );

  return (
    <Popover modal={false} onOpenChange={handleMenuOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" className="btn btn-sm btn-light mr-2">
          <KeenIcon icon="element-5" className="text-[18px]" />
          Customize Columns
        </button>
      </PopoverTrigger>

      {/* <Popover.Portal> */}
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={15}
        className="w-[19rem] bg-[--tw-navbar-bg] dark:bg-[--tw-navbar-bg-dark]
          border rounded-lg shadow-lg
          py-2 z-50 !px-2"
      >
        {/* Header with search + actions */}
        <div className="pb-2 sticky top-0 bg-[--tw-navbar-bg] dark:bg-[--tw-navbar-bg-dark] z-10 border-b">
          <input
            type="text"
            placeholder={formatMessage({ id: 'REFERRED.SEARCH_COLUMNS' })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input"
          />
          <div className="flex flex-wrap justify-end gap-2 mt-2">
            <button
              onClick={handleSelectAll}
              className="btn btn-xs btn-outline btn-primary"
            >
              <FormattedMessage id="REFERRED.SELECT_ALL" />
            </button>
            <button
              onClick={handleResetDefault}
              className="btn btn-xs btn-outline btn-danger"
            >
              Reset selection
            </button>
            <button
              onClick={handleResetOrdering}
              className="btn btn-xs btn-outline btn-light"
            >
              Reset Ordering
            </button>
          </div>
        </div>

        {/* Column list */}
        <div className="divide-y-2 max-h-64 overflow-y-auto overflow-x-hidden px-1 custom-scrollbar">
          {filteredColumns.length === 0 ? (
            <div className="text-sm text-center py-2">
              No columns found
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              onDragEnd={(event) => {
                const { active, over } = event;
                if (!over || active.id === over.id) return;
                const oldIndex = orderedCols.findIndex((c) => c.name === active.id);
                const newIndex = orderedCols.findIndex((c) => c.name === over.id);
                if (oldIndex === -1 || newIndex === -1) return;
                const next = normalizePinned(arrayMove(orderedCols, oldIndex, newIndex));
                setOrderedCols(next);
                applyColumnOrder(next, true);
              }}
            >
              <SortableContext
                items={orderedCols.map((c) => c.name)}
                strategy={verticalListSortingStrategy}
              >
                {filteredColumns.map((c) => (
                  <SortableRow
                    key={c.name}
                    id={c.name}
                    title={c.title}
                    checked={visibleCols.includes(c.name)}
                    onToggle={() => handleToggle(c.name)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </PopoverContent>
      {/* </Popover.Portal> */}
    </Popover>
  );
};

export { ColumnToggleDropdown };

const SortableRow = ({ id, title, checked, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-center justify-between gap-2 px-2 py-2 text-sm text-gray-700
        hover:rounded-md hover:bg-gray-200 dark:hover:bg-dark cursor-pointer transition-colors duration-150"
    >
      <div
        className="text-gray-500 mr-2 cursor-grab active:cursor-grabbing select-none"
        {...attributes}
        {...listeners}
        aria-label="Reorder column"
      >
        ⋮⋮
      </div>
      <label htmlFor={id} className="truncate w-full cursor-pointer">
        {title}
      </label>
      <div className="relative flex items-center justify-between">
        <input
          id={id}
          name="column-item"
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="peer appearance-none h-5 w-5 border border-gray-400 rounded-md
            cursor-pointer transition-all duration-200
            checked:bg-blue-600 checked:border-blue-600
            hover:border-blue-500 focus:outline-none"
        />
        <svg
          onClick={onToggle}
          className="absolute -translate-y-1/2 -translate-x-1/2
            top-1/2 left-1/2 size-3 text-white bg0red opacity-0
            peer-checked:opacity-100 transition-opacity duration-200"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
    </div>
  );
};
